export interface FlowDeviceReference {
	id: string;
	channel: string;
	name?: string;
}

export interface FlowConditionCapabilitySelection {
	device: FlowDeviceReference | null;
	property: string;
	operator: string;
	value: unknown;
}

export interface FlowActionCapabilitySelection {
	device: FlowDeviceReference | null;
	actions: Array<{ name: string; value: unknown }>;
}

/**
 * Normalizes the current wizard model, imported scene model, and V2 capability model into one
 * stable shape. Card definitions can consume this without knowing which editor produced it.
 */
export function resolveFlowDeviceReference(data: Record<string, any>): FlowDeviceReference | null {
	const condition = data._device_condition || {};
	const capability = data.sceneCapability || {};
	const source = data.device || condition.device || capability.device;
	const id = String(source?.id || condition.deviceId || capability.deviceId || '').trim();
	if (!id) return null;
	return {
		id,
		channel: String(source?.channel ?? condition.channel ?? capability.channel ?? '0'),
		...(source?.name || data.deviceName ? { name: String(source?.name || data.deviceName) } : {}),
	};
}

export function resolveConditionCapabilitySelection(
	data: Record<string, any>,
): FlowConditionCapabilitySelection {
	const condition = data._device_condition || {};
	const capability = data.sceneCapability || {};
	const property = String(
		data.cond_name ||
			condition.cond_name ||
			capability.capabilityName ||
			capability.name ||
			data.property?.id ||
			'',
	);
	return {
		device: resolveFlowDeviceReference(data),
		property,
		operator: String(data.operator || condition.operator || capability.operator || data.cmpop || ''),
		value: data.value ?? condition.value ?? capability.value ?? data.cmpval,
	};
}

export function resolveActionCapabilitySelection(
	data: Record<string, any>,
): FlowActionCapabilitySelection {
	return {
		device: resolveFlowDeviceReference(data),
		actions: Array.isArray(data.actions)
			? data.actions
					.filter((action: any) => action?.name)
					.map((action: any) => ({ name: String(action.name), value: action.value }))
			: [],
	};
}
